import { Accordion, Avatar, Media } from "@ameelio/ui";
import GroupsOutlined from "@mui/icons-material/GroupsOutlined";
import { Box, Typography } from "@mui/material";
import { SystemStyleObject, Theme } from "@mui/system";
import { useTranslation } from "react-i18next";
import {
  Call,
  CallParticipant,
  ParticipantRole,
  ParticipantType,
} from "../api/graphql";

type Props = {
  currentParticipant: Pick<CallParticipant, "externalId">;
  call: Pick<
    Call,
    "id" | "features" | "callType" | "callStatus" | "startedAt" | "endedAt"
  > & {
    participants: Pick<
      CallParticipant,
      "externalId" | "fullName" | "role" | "type"
    >[];
  };
  onChange: (expanded: boolean) => void;
  textColor?: { primary?: string; secondary?: string };
  sx?: SystemStyleObject<Theme>;
  expandIconSx?: SystemStyleObject<Theme>;
};

export default function MeetingParticipantsAccordion({
  currentParticipant,
  call,
  onChange,
  textColor,
  sx,
  expandIconSx,
}: Props) {
  const { t } = useTranslation();
  const hosts = call.participants.filter(
    (c) => c.role === ParticipantRole.WebinarHost
  );
  const students = call.participants.filter((c) =>
    [
      ParticipantRole.WebinarAttendee,
      ParticipantRole.WebinarIsolatedAttendee,
    ].includes(c.role)
  );
  const attendees = [...hosts, ...students];
  return (
    <Accordion
      title={t("Attendees")}
      titleImage={<GroupsOutlined />}
      numItems={attendees.length}
      textColor={textColor}
      expandIconSx={expandIconSx}
      sx={sx}
      onChange={onChange}
      details={
        <>
          {attendees.map((c) => (
            <Box
              key={c.externalId}
              sx={{
                p: 1,
                pl: 5,
              }}
            >
              <Media image={<Avatar contact={c} />}>
                <Typography
                  variant="subtitle2"
                  sx={{ color: textColor?.primary || "text.primary" }}
                >
                  {c.fullName}{" "}
                  {currentParticipant.externalId === c.externalId && t("(you)")}
                  {currentParticipant.externalId !== c.externalId &&
                    c.type === ParticipantType.User &&
                    t("(co-host)")}
                </Typography>
              </Media>
            </Box>
          ))}
        </>
      }
    />
  );
}
